import { invalidateMatterAndSearchQueries } from "@/lib/data/react-query/search/invalidation";
import {
  type ProviderChipVariant,
  providerChipVariants,
} from "@/components/features/matter/medical-treatments/MatterTreatmentAppointmentsCard/MatterTreatmentAppointmentsCard";
import { MatterTreatmentPlanContextMenu } from "@/components/features/matter/context-menus/MatterTreatmentPlanContextMenu";
import {
  ActionConfirmationModal,
  AppModal,
  Badge,
  Button,
  ExpandableText,
  Input,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
} from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/shadcn/DropdownMenu/DropdownMenu";
import { useMedicalTreatmentViewModel } from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import { useUser } from "@/components/UserProvider";
import { useDeleteMatterContactConnection } from "@/lib/data/react-query/matters/matter-contact-connection";
import {
  useCreateMedicalTreatmentPlan,
  useDeleteMedicalTreatmentPlan,
  useUpdateMedicalTreatmentPlan,
} from "@/lib/data/react-query/matters/medical-treatment-plan";
import { type CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS } from "@/lib/data/static/contactStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Connection, Matter, MedicalTreatmentPlan } from "@/lib/types";
import type { MatterContactConnection } from "@/lib/types/Matter";
import { cn } from "@/lib/utils/cn";
import { formatContactsName } from "@/lib/utils/format";
import {
  getConnectionContactName,
  syncSingleContactConnection,
} from "@/lib/utils/connections/item-contact-connections";
import {
  getPlanFulfillerConnection,
  PLAN_FULFILLER_CONNECTION_TYPE,
} from "@/lib/utils/connections/medical-treatment-subitem-contact-connections";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { CheckCircle2, Circle, ExternalLink, MoreVertical } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

type TreatmentPlanViewItem = MedicalTreatmentPlan & {
  provider?: Connection | null;
  fulfiller?: Connection | MatterContactConnection | null;
  type: Array<
    (typeof CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS)[number]
  >;
};

function getTreatmentTypes(treatment: Matter["medicalTreatments"][number]) {
  return treatment.type;
}

interface MatterOverviewTreatmentPlansCardProps {
  initialPlanId?: string;
  matter: Matter;
  showHeader?: boolean;
  showCreateButton?: boolean;
  sortOrder?: "asc" | "desc";
  variant?: "overview" | "chronology";
  enableVariantTabs?: boolean;
}

export const MatterOverviewTreatmentPlansCard = ({
  initialPlanId,
  matter,
  showHeader = true,
  showCreateButton = false,
  sortOrder = "desc",
  variant = "overview",
  enableVariantTabs = false,
}: MatterOverviewTreatmentPlansCardProps) => {
  const { hasActionPermission } = useUser();
  const navigate = useNavigate({ from: "/matters/$matterId" });
  const queryClient = useQueryClient();
  const completeMedicalTreatments = useMedicalTreatmentViewModel(matter);
  const createPlan = useCreateMedicalTreatmentPlan({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const updatePlan = useUpdateMedicalTreatmentPlan({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const deletePlan = useDeleteMedicalTreatmentPlan();
  const deleteMatterContactConnection = useDeleteMatterContactConnection({
    showSuccessToast: false,
  });
  const canDeletePlans = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentPlan,
    ACTION_PERMISSION_VALUES.delete,
  );

  const [activeVariant, setActiveVariant] =
    useState<MatterOverviewTreatmentPlansCardProps["variant"]>(variant);
  const [filter, setFilter] = useState<"outstanding" | "all" | "complete">(
    "outstanding",
  );
  const [editingPlan, setEditingPlan] = useState<TreatmentPlanViewItem | null>(
    null,
  );
  const [editingPlanName, setEditingPlanName] = useState("");
  const [editingPlanStatus, setEditingPlanStatus] = useState<
    "Incomplete" | "Complete"
  >("Incomplete");
  const [deletingPlan, setDeletingPlan] =
    useState<TreatmentPlanViewItem | null>(null);
  const [isSavingPlanWorkflow, setIsSavingPlanWorkflow] = useState(false);
  const [openPlanMenuId, setOpenPlanMenuId] = useState<string | null>(null);
  const openedInitialPlanId = useRef<string | null>(null);

  const UNASSIGNED_PROVIDER_KEY = "__unassigned_provider__";

  const getProviderKey = (provider?: Connection | null) =>
    provider?.targetObjectId ||
    provider?.id ||
    (provider ? getConnectionContactName(provider.contact) : undefined) ||
    UNASSIGNED_PROVIDER_KEY;

  const getProviderName = (provider?: Connection | null) =>
    (provider ? getConnectionContactName(provider.contact) : undefined) ||
    "Provider not assigned";
  const getFulfillerName = (
    fulfiller?: Connection | MatterContactConnection | null,
  ) =>
    (fulfiller
      ? "targetObjectId" in fulfiller
        ? getConnectionContactName(fulfiller.contact ?? undefined)
        : formatContactsName(fulfiller.contact)
      : undefined) || null;

  const getPlanFulfiller = (plan: MedicalTreatmentPlan) =>
    getPlanFulfillerConnection(plan) ?? null;

  const getPlanSortValue = (
    planDate?: string | null,
    order: "asc" | "desc" = "desc",
  ) => {
    if (planDate && dayjs(planDate).isValid()) {
      return dayjs(planDate).valueOf();
    }
    return order === "desc"
      ? Number.NEGATIVE_INFINITY
      : Number.POSITIVE_INFINITY;
  };

  const comparePlans = (
    aDate?: string | null,
    bDate?: string | null,
    order: "asc" | "desc" = "desc",
  ) => {
    const aValue = getPlanSortValue(aDate, order);
    const bValue = getPlanSortValue(bDate, order);
    return order === "desc" ? bValue - aValue : aValue - bValue;
  };

  useEffect(() => {
    if (!enableVariantTabs) {
      setActiveVariant(variant);
    }
  }, [variant, enableVariantTabs]);

  useEffect(() => {
    if (enableVariantTabs) {
      setActiveVariant(variant);
    }
  }, [enableVariantTabs, variant]);

  const effectiveVariant = enableVariantTabs ? activeVariant : variant;

  const providerVariantMap = (() => {
    const providers = completeMedicalTreatments
      .map((t) => t.provider)
      .filter(Boolean) as NonNullable<
      (typeof completeMedicalTreatments)[number]["provider"]
    >[];
    const getKey = (p: (typeof providers)[number]) =>
      p.targetObjectId || p.id || getConnectionContactName(p.contact);
    const deduped = Array.from(
      new Map(providers.map((p) => [getKey(p), p])).values(),
    );
    deduped.sort(
      (a, b) => dayjs(a.dateCreated).unix() - dayjs(b.dateCreated).unix(),
    );
    const map: Record<string, ProviderChipVariant> = {};
    deduped.forEach((p, idx) => {
      map[getKey(p) ?? UNASSIGNED_PROVIDER_KEY] =
        providerChipVariants[idx % providerChipVariants.length];
    });
    map[UNASSIGNED_PROVIDER_KEY] =
      map[UNASSIGNED_PROVIDER_KEY] ?? providerChipVariants[0];
    return map;
  })();

  const providerGroups = completeMedicalTreatments
    .map((treatment) => {
      const provider = treatment.provider ?? null;
      const providerKey = getProviderKey(provider);
      const treatmentTypes = getTreatmentTypes(treatment);
      const plans: TreatmentPlanViewItem[] = treatment.plans
        .map((plan) => ({
          ...plan,
          provider,
          fulfiller: getPlanFulfiller(plan),
          type: treatmentTypes,
        }))
        .sort((a, b) => comparePlans(a.date, b.date, sortOrder));
      return {
        provider,
        providerKey,
        providerName: getProviderName(provider),
        plans,
        treatmentType: treatmentTypes,
      };
    })
    .filter((group) => group.plans.length > 0);

  const attachedPlanIds = new Set(
    matter.medicalTreatmentAppointmentConnections.flatMap((connection) =>
      connection.medicalTreatmentPlanId
        ? [connection.medicalTreatmentPlanId]
        : [],
    ),
  );
  const unattachedPlans: TreatmentPlanViewItem[] = matter.medicalTreatmentPlans
    .filter((plan) => !attachedPlanIds.has(plan.id))
    .map((plan) => ({
      ...plan,
      provider: null,
      fulfiller: getPlanFulfiller(plan),
      type: [],
    }));
  const providerGroupsWithUnattached =
    unattachedPlans.length > 0
      ? [
          ...providerGroups,
          {
            provider: null,
            providerKey: UNASSIGNED_PROVIDER_KEY,
            providerName: "Provider not assigned",
            plans: unattachedPlans.sort((a, b) =>
              comparePlans(a.date, b.date, sortOrder),
            ),
            treatmentType: [] as Array<
              (typeof CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS)[number]
            >,
          },
        ]
      : providerGroups;

  const allPlans = providerGroupsWithUnattached.flatMap((group) => group.plans);

  const chronologyPlans = [...allPlans].sort((a, b) =>
    comparePlans(a.date, b.date, "desc"),
  );

  const hasPlans = allPlans.length > 0;
  const injuryDetails =
    matter.clientInjuryDescription?.trim() ||
    matter.incidentInjuryDescription?.trim() ||
    "";

  const earliestPlanDateValue = (() => {
    const datedPlans = allPlans.filter(
      (plan) => plan.date && dayjs(plan.date).isValid(),
    );
    if (datedPlans.length === 0) {
      return undefined;
    }
    const earliestPlan = datedPlans.reduce((earliest, plan) => {
      if (comparePlans(plan.date, earliest.date, "asc") < 0) {
        return plan;
      }
      return earliest;
    });
    return earliestPlan.date;
  })();

  const treatmentStartedDisplay = earliestPlanDateValue
    ? dayjs(earliestPlanDateValue).format("MMM D, YYYY")
    : "-";
  const isPlanModalOpen = Boolean(editingPlan);
  const isCreatingPlan = editingPlan?.id === "";
  const isPlanMutationPending =
    isSavingPlanWorkflow ||
    createPlan.isPending ||
    updatePlan.isPending ||
    deleteMatterContactConnection.isPending;

  const clearPlanFulfillerConnections = async (planId: string) => {
    const plan = matter.medicalTreatmentPlans.find(
      (item) => item.id === planId,
    );

    await syncSingleContactConnection({
      matterId: matter.id,
      currentConnections: plan?.connections,
      connectionType: PLAN_FULFILLER_CONNECTION_TYPE,
      current: getPlanFulfillerConnection(plan),
      desiredContactId: undefined,
      sourceObjectId: planId,
      sourceObjectType: "matter_medical_treatment_plan",
    });
  };

  const markPlanCompleted = async (plan: (typeof chronologyPlans)[number]) => {
    if (plan.status === "Completed") {
      return;
    }

    try {
      await updatePlan.mutateAsync({
        matterId: matter.id,
        id: plan.id,
        dateModified: plan.dateModified,
        date: plan.date,
        description: plan.description,
        status: "Completed",
      });
      await invalidateMatterAndSearchQueries(queryClient);
      toast.success("Treatment plan marked as completed");
    } catch (error) {
      console.error(error);
    }
  };

  const markPlanIncomplete = async (plan: (typeof chronologyPlans)[number]) => {
    if (plan.status !== "Completed") {
      return;
    }

    try {
      await updatePlan.mutateAsync({
        matterId: matter.id,
        id: plan.id,
        dateModified: plan.dateModified,
        date: plan.date,
        description: plan.description,
        status: "Not Started",
      });
      await clearPlanFulfillerConnections(plan.id);
      await invalidateMatterAndSearchQueries(queryClient);
      toast.success("Treatment plan marked as incomplete");
    } catch (error) {
      console.error(error);
    }
  };

  const openEditPlanModal = (plan: TreatmentPlanViewItem) => {
    setOpenPlanMenuId(null);
    setEditingPlan(plan);
    setEditingPlanName(plan.description ?? "");
    setEditingPlanStatus(
      plan.status === "Completed" ? "Complete" : "Incomplete",
    );
  };

  useEffect(() => {
    if (!initialPlanId || openedInitialPlanId.current === initialPlanId) {
      return;
    }
    const initialPlan = allPlans.find((plan) => plan.id === initialPlanId);
    if (!initialPlan) {
      return;
    }
    openedInitialPlanId.current = initialPlanId;
    setEditingPlan(initialPlan);
    setEditingPlanName(initialPlan.description ?? "");
    setEditingPlanStatus(
      initialPlan.status === "Completed" ? "Complete" : "Incomplete",
    );
  }, [allPlans, initialPlanId]);

  const openDeletePlanModal = (plan: TreatmentPlanViewItem) => {
    setOpenPlanMenuId(null);
    window.setTimeout(() => {
      setDeletingPlan(plan);
    }, 0);
  };

  const openCreatePlanModal = () => {
    setEditingPlan({
      id: "",
      matterId: matter.id,
      date: "",
      description: "",
      status: "Not Started",
      dateCreated: "",
      dateModified: "",
      connections: [],
      provider: null,
      type: [],
    });
    setEditingPlanName("");
    setEditingPlanStatus("Incomplete");
  };

  const closeEditPlanModal = () => {
    setEditingPlan(null);
    setEditingPlanName("");
    setEditingPlanStatus("Incomplete");
  };

  const deleteEditingPlan = async () => {
    if (!deletingPlan) {
      return;
    }

    try {
      await deletePlan.mutateAsync({
        matterId: matter.id,
        id: deletingPlan.id,
      });
      if (editingPlan?.id === deletingPlan.id) {
        closeEditPlanModal();
      }
      setDeletingPlan(null);
    } catch (error) {
      console.error(error);
    }
  };

  const saveEditedPlan = async () => {
    if (!editingPlan || !editingPlanName.trim()) {
      return;
    }

    setIsSavingPlanWorkflow(true);
    try {
      if (isCreatingPlan) {
        await createPlan.mutateAsync({
          matterId: matter.id,
          date: dayjs().format("YYYY-MM-DD"),
          description: editingPlanName.trim(),
          status:
            editingPlanStatus === "Complete" ? "Completed" : "Not Started",
        });
        closeEditPlanModal();
        toast.success("Treatment plan created successfully");
      } else {
        await updatePlan.mutateAsync({
          matterId: matter.id,
          id: editingPlan.id,
          dateModified: editingPlan.dateModified,
          date: editingPlan.date,
          description: editingPlanName.trim(),
          status:
            editingPlanStatus === "Complete" ? "Completed" : "Not Started",
        });
        if (editingPlanStatus !== "Complete") {
          await clearPlanFulfillerConnections(editingPlan.id);
        }
        closeEditPlanModal();
        toast.success("Treatment plan updated successfully");
      }
      void invalidateMatterAndSearchQueries(queryClient);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSavingPlanWorkflow(false);
    }
  };

  const tabProviderGroups = providerGroupsWithUnattached
    .map((group) => ({
      ...group,
      plans: group.plans.filter((plan) => {
        const isComplete = plan.status === "Completed";
        if (filter === "outstanding") return !isComplete;
        if (filter === "complete") return isComplete;
        return true;
      }),
    }))
    .filter((group) => group.plans.length > 0);

  const hasTabPlans = tabProviderGroups.length > 0;

  if (!showHeader) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <Text variant="body" className="text-lg font-semibold">
            Treatment plan
          </Text>
          <div className="flex items-center gap-2">
            {showCreateButton && (
              <Button type="button" size="sm" onClick={openCreatePlanModal}>
                Add plan item
              </Button>
            )}
            <div className="bg-muted/50 border-border flex items-center gap-1 rounded-md border p-1">
              {(["outstanding", "all", "complete"] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    setFilter(option);
                  }}
                  className={cn(
                    "rounded px-3 py-1.5 text-xs font-medium transition-colors",
                    filter === option
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {!hasTabPlans ? (
          <Text variant="subHeading" className="py-8 text-center">
            No treatment plan items found
          </Text>
        ) : (
          <div className="space-y-4">
            {tabProviderGroups.map((group) => (
              <div key={group.providerKey} className="space-y-2 rounded-lg">
                <div className="flex items-center justify-between px-1">
                  <Text
                    variant="caption"
                    color="muted"
                    className="tracking-wider uppercase"
                  >
                    {group.providerName} · {group.plans.length} item
                    {group.plans.length !== 1 ? "s" : ""}
                  </Text>
                </div>

                <div className="divide-border border-border divide-y overflow-hidden rounded-lg border">
                  {group.plans.map((plan) => {
                    const isComplete = plan.status === "Completed";
                    const fulfillerName = getFulfillerName(plan.fulfiller);

                    return (
                      <MatterTreatmentPlanContextMenu
                        key={plan.id}
                        canDelete={canDeletePlans}
                        date={plan.date}
                        description={plan.description}
                        id={plan.id}
                        isDeleting={deletePlan.isPending}
                        matter={matter}
                        onDelete={async () => {
                          await deletePlan.mutateAsync({
                            matterId: matter.id,
                            id: plan.id,
                          });
                        }}
                        onEdit={() => {
                          openEditPlanModal(plan);
                        }}
                        status={plan.status}
                        trigger={
                          <div
                            className={cn(
                              "group hover:bg-muted/50 flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors",
                              isComplete && "bg-muted/30",
                            )}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                if (!isComplete) {
                                  void markPlanCompleted(plan);
                                }
                              }}
                              className="text-muted-foreground hover:text-foreground mt-0.5 shrink-0 transition-colors"
                            >
                              {isComplete ? (
                                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                              ) : (
                                <Circle className="h-5 w-5" />
                              )}
                            </button>

                            <div className="min-w-0 flex-1">
                              <Text variant="body">
                                {plan.description?.trim() ||
                                  "Treatment plan item"}
                              </Text>
                              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                <Text variant="caption" color="muted" as="span">
                                  {plan.date
                                    ? `Added on ${dayjs(plan.date).format("MMM D")}`
                                    : "Treatment plan update"}
                                </Text>
                                {isComplete && (
                                  <Text
                                    variant="caption"
                                    color="muted"
                                    as="span"
                                  >
                                    {`Completed ${dayjs(plan.dateModified).format("MMM D")}${fulfillerName ? ` by ${fulfillerName}` : ""}`}
                                  </Text>
                                )}
                              </div>
                            </div>

                            <DropdownMenu
                              open={openPlanMenuId === plan.id}
                              onOpenChange={(open) => {
                                setOpenPlanMenuId(open ? plan.id : null);
                              }}
                            >
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1.5 opacity-0 transition-opacity group-hover:opacity-100"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-40">
                                {isComplete && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setOpenPlanMenuId(null);
                                      void markPlanIncomplete(plan);
                                    }}
                                  >
                                    Mark incomplete
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => {
                                    openEditPlanModal(plan);
                                  }}
                                >
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => {
                                    openDeletePlanModal(plan);
                                  }}
                                >
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        }
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        <AppModal
          open={isPlanModalOpen}
          onOpenChange={(open) => {
            if (!open) {
              closeEditPlanModal();
            }
          }}
          title={
            isCreatingPlan
              ? "Create treatment plan item"
              : "Edit treatment plan item"
          }
          size="md"
          loading={isPlanMutationPending}
          footer={
            <div className="flex w-full justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={closeEditPlanModal}
                disabled={isPlanMutationPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  void saveEditedPlan();
                }}
                disabled={!editingPlanName.trim() || isPlanMutationPending}
              >
                {isCreatingPlan ? "Create" : "Save"}
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="treatment-plan-item-name">Item name</Label>
              <Input
                id="treatment-plan-item-name"
                value={editingPlanName}
                onChange={(e) => {
                  setEditingPlanName(e.target.value);
                }}
                placeholder="Treatment plan item name"
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <div className="bg-muted/50 border-border flex items-center gap-1 rounded-md border p-1">
                {(["Incomplete", "Complete"] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      setEditingPlanStatus(option);
                    }}
                    className={cn(
                      "rounded px-3 py-2 text-sm font-medium transition-colors",
                      editingPlanStatus === option
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </AppModal>
        <ActionConfirmationModal
          open={Boolean(deletingPlan)}
          setOpen={(open) => {
            if (!open && !deletePlan.isPending) {
              setDeletingPlan(null);
            }
          }}
          title="Delete treatment plan item"
          description="Are you sure you want to delete this treatment plan item? This action cannot be undone."
          buttonText="Delete"
          isActionPending={deletePlan.isPending}
          afterActionFn={() => {
            void deleteEditingPlan();
          }}
        />
      </div>
    );
  }

  const renderPlan = (
    plan: (typeof chronologyPlans)[number],
    {
      showProviderBadge = false,
      providerOverride,
      providerKeyOverride,
      showTreatmentType = true,
      treatmentTypeOverride,
    }: {
      showProviderBadge?: boolean;
      providerOverride?: Connection | null;
      providerKeyOverride?: string;
      showTreatmentType?: boolean;
      treatmentTypeOverride?: Array<
        (typeof CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS)[number]
      >;
    } = {},
  ) => {
    const formattedPlanDate = plan.date
      ? dayjs(plan.date).format("MMM D, YYYY")
      : undefined;
    const planStatus = plan.status;
    const planDescription = plan.description;
    const providerForDisplay = providerOverride ?? plan.provider ?? null;
    const providerKey =
      providerKeyOverride ?? getProviderKey(providerForDisplay);
    const badgeVariant =
      providerVariantMap[providerKey] ?? providerChipVariants[0];
    const providerName = getProviderName(providerForDisplay);
    const fulfillerName = getFulfillerName(plan.fulfiller);
    const treatmentType = treatmentTypeOverride ?? plan.type;

    const hasBodyContent =
      Boolean(planStatus) ||
      Boolean(planDescription) ||
      Boolean(formattedPlanDate);

    return (
      <div key={plan.id} className={cn("rounded-lg p-3")}>
        <div className="flex flex-wrap items-center gap-2">
          {showProviderBadge && (
            <Badge variant={badgeVariant} className="h-6">
              {providerName}
            </Badge>
          )}
          {showTreatmentType && treatmentType.length > 0 && (
            <Text variant="subHeading">• {treatmentType.join(", ")}</Text>
          )}
        </div>
        {hasBodyContent && (
          <div
            className={cn(
              "mt-2 space-y-2",
              showProviderBadge && "pl-4 sm:pl-5 md:pl-6",
            )}
          >
            {(planStatus || planDescription) && (
              <div className="space-y-1">
                {planDescription ? (
                  <ExpandableText
                    text={planDescription}
                    maxLines={3}
                    className="text-muted-foreground text-sm"
                  />
                ) : (
                  <Text variant="subHeading">No description</Text>
                )}
              </div>
            )}
            {(formattedPlanDate || planStatus) && (
              <div className="flex items-center gap-2">
                {formattedPlanDate && (
                  <Text variant="body" color="primary">
                    {formattedPlanDate}
                  </Text>
                )}
                {planStatus && (
                  <Text variant="subHeading" as="span">
                    {formattedPlanDate ? `• ${planStatus}` : planStatus}
                  </Text>
                )}
                {planStatus === "Completed" && fulfillerName && (
                  <Text variant="subHeading" as="span">
                    • Completed by {fulfillerName}
                  </Text>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={cn("space-y-2")}>
      <div className="flex items-center justify-between">
        <Text variant="title">Treatment plan</Text>
        <Button
          asChild
          variant="ghost"
          size="icon"
          className="h-4 w-4 cursor-pointer"
          onClick={() => {
            void navigate({
              to: `/matters/$matterId/medical-treatments`,
              params: { matterId: matter.id },
            });
          }}
        >
          <ExternalLink />
        </Button>
      </div>
      <div>
        {injuryDetails && (
          <Text
            variant="subHeading"
            className="line-clamp-2 whitespace-pre-wrap"
          >
            {injuryDetails}
          </Text>
        )}
        <Text variant="subHeading">
          <span className="font-semibold">Treatment started:</span>{" "}
          {treatmentStartedDisplay}
        </Text>
      </div>
      {enableVariantTabs && (
        <Tabs
          value={effectiveVariant}
          onValueChange={(value) => {
            setActiveVariant(value as "overview" | "chronology");
          }}
          className="pt-1"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="overview">By provider</TabsTrigger>
            <TabsTrigger value="chronology">Chronology</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {!hasPlans ? (
        <div className={cn("mt-4")}>
          <Text variant="subHeading">No treatment plans found.</Text>
        </div>
      ) : effectiveVariant === "chronology" ? (
        <div className={cn("mt-4 space-y-3")}>
          {chronologyPlans.map((plan) =>
            renderPlan(plan, { showProviderBadge: true }),
          )}
        </div>
      ) : (
        <div className={cn("mt-4 space-y-5")}>
          {providerGroups.map((group) => (
            <div key={group.providerKey} className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  variant={
                    providerVariantMap[group.providerKey] ??
                    providerChipVariants[0]
                  }
                  className="h-6"
                >
                  {group.providerName}
                </Badge>
                <Text variant="subHeading">
                  • {group.treatmentType.join(", ")}
                </Text>
              </div>
              <div className="border-border/60 space-y-3 border-l pl-3 sm:pl-4 md:pl-6">
                {group.plans.map((plan) =>
                  renderPlan(plan, {
                    showProviderBadge: false,
                    showTreatmentType: false,
                    providerOverride: group.provider,
                    providerKeyOverride: group.providerKey,
                    treatmentTypeOverride: group.treatmentType,
                  }),
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
